/**
 * PasswordStrengthMeter — visual real-time password strength indicator
 * Shows score bar + requirements checklist per active policy
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X, ShieldCheck, ShieldAlert } from 'lucide-react';
import { API_BASE, API } from '../../lib/api';


const LEVEL_CONFIG = {
  'Very Weak': { color: '#FF5A6A', bg: '#FF5A6A', width: '10%' },
  'Weak':      { color: '#FF5A6A', bg: '#FF8C42', width: '25%' },
  'Medium':    { color: '#F5C451', bg: '#F5C451', width: '50%' },
  'Strong':    { color: '#3DD56D', bg: '#3DD56D', width: '75%' },
  'Very Strong':{ color: '#3DD56D', bg: '#3DD56D', width: '100%' },
};

export default function PasswordStrengthMeter({ password, userType = 'M', clientId = '', slno, className = '' }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!password) { setResult(null); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/cfr21/check-password`, { password, user_type: userType, client_id: clientId, slno });
        setResult(data);
      } catch {
        // Fallback: local scoring
        let score = 0;
        if (password.length >= 8)  score++;
        if (password.length >= 12) score++;
        if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        const levels = ['Very Weak','Weak','Medium','Strong','Very Strong'];
        setResult({ score, level: levels[Math.min(score, 4)], valid: score >= 3, errors: [] });
      } finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [password, userType, clientId, slno]);

  if (!password) return null;

  const level = result?.level || 'Very Weak';
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG['Very Weak'];
  const requirements = result?.policy ? [
    { label: `Min ${result.policy.min_length} characters`, met: password.length >= result.policy.min_length },
    { label: 'Uppercase letter (A-Z)', met: /[A-Z]/.test(password), needed: result.policy.require_uppercase },
    { label: 'Lowercase letter (a-z)', met: /[a-z]/.test(password), needed: result.policy.require_lowercase },
    { label: 'Number (0-9)', met: /[0-9]/.test(password), needed: result.policy.require_numbers },
    { label: 'Special character (!@#...)', met: /[^A-Za-z0-9]/.test(password), needed: result.policy.require_special },
  ].filter(r => r.needed !== false) : [
    { label: 'Min 8 characters', met: password.length >= 8 },
    { label: 'Uppercase + lowercase', met: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: 'Number', met: /[0-9]/.test(password) },
    { label: 'Special character', met: /[^A-Za-z0-9]/.test(password) },
  ];

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full dark:bg-white/[0.08] bg-gray-200 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-300" style={{ width: cfg.width, backgroundColor: cfg.bg }} />
        </div>
        <span className="text-[11px] font-medium flex-shrink-0" style={{ color: cfg.color, fontFamily: "'IBM Plex Mono',monospace" }}>
          {loading ? '...' : level}
        </span>
        {result?.valid
          ? <ShieldCheck size={13} className="text-[#3DD56D] flex-shrink-0" />
          : <ShieldAlert size={13} className="text-[#FF5A6A] flex-shrink-0" />}
      </div>

      {/* Requirements checklist */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {requirements.map((r, i) => (
          <div key={i} className="flex items-center gap-1">
            {r.met
              ? <Check size={10} className="text-[#3DD56D] flex-shrink-0" />
              : <X     size={10} className="text-[#FF5A6A] flex-shrink-0" />}
            <span className={`text-[10px] ${ r.met ? 'dark:text-neutral-400 text-gray-500' : 'dark:text-neutral-500 text-gray-400' }`}>{r.label}</span>
          </div>
        ))}
      </div>

      {/* Server-side errors */}
      {result?.errors?.length > 0 && (
        <div className="space-y-0.5">
          {result.errors.map((err, i) => (
            <p key={i} className="text-[11px] text-[#FF5A6A]">{err}</p>
          ))}
        </div>
      )}
    </div>
  );
}
